import axios from 'axios'
import { API_BASE_URL, API_ENDPOINTS } from '../config/api'
import { store } from '../store/store'
import { logout } from '../store/slices/authSlice'
import { isOwnerRole } from '../utils/roles'

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const activeRole = localStorage.getItem('activeRole')
    let token = null

    if (isOwnerRole(activeRole)) {
      token = localStorage.getItem('ownerToken')
    } else if (activeRole === 'staff') {
      token = localStorage.getItem('staffToken')
    } else {
      // Fallback for backward compatibility
      token = localStorage.getItem('token')
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Handle 304 Not Modified - browser cached response, but we still need the data
    // Axios should handle this, but ensure we return the response
    if (response.status === 304 && response.data) {
      return response
    }
    return response
  },
  (error) => {
    // Handle 401 - Token expired or unauthorized
    if (error.response?.status === 401) {
      console.log('401 Unauthorized - Auto logging out...')
      // Dispatch logout action to clear Redux state
      store.dispatch(logout())

      // Clear localStorage
      localStorage.removeItem('token')
      localStorage.removeItem('ownerToken')
      localStorage.removeItem('staffToken')
      localStorage.removeItem('activeRole')
      localStorage.removeItem('user')
      localStorage.removeItem('loginConfirmationPending')
      sessionStorage.clear()

      // Redirect to login page
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export const getSportsTree = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.GET_SPORTS_TREE)
    return response.data
  } catch (error) {
    console.error('Error fetching sports tree:', error)
    throw error
  }
}

export const getSportsData = async (id) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_SPORT_DATA, { id })
    return response.data
  } catch (error) {
    console.error('Error fetching sports data:', error)
    throw error
  }
}

export const getMatchPrivateData = async (gmid, sid) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_MATCH_PRIVATE_DATA, { gmid, sid })
    return response.data
  } catch (error) {
    console.error('Error fetching match private data:', error)
    throw error
  }
}

export const getSportsScoreCard = async (gmid, sid) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_SPORTS_SCORE_CARD, { gmid, sid })
    if (response.data && response.data.success && response.data.data?.score_url) {
      return response.data.data.score_url
    }
    return null
  } catch (error) {
    console.error('Error fetching scorecard:', error)
    return null
  }
}

export const getBetList = async (params) => {
  try {
    const response = await api.get(API_ENDPOINTS.LORDS.BET_LIST, { params })
    return response.data
  } catch (error) {
    console.error('Error fetching bet list:', error)
    throw error
  }
}
export const getBetTicker = async (params) => {
  try {
    const response = await api.get(API_ENDPOINTS.LORDS.BET_TICKER, { params })
    return response.data
  } catch (error) {
    console.error('Error fetching bet ticker:', error)
    throw error
  }
}

export const getGameReport = async (params) => {
  try {
    const response = await api.post(API_ENDPOINTS.REPORTS.GAME_REPORT, params)
    return response.data
  } catch (error) {
    console.error('Error fetching game report:', error)
    throw error
  }
}

export const searchByUsername = async (username, page = 1, limit = 10) => {
  try {
    const response = await api.get(API_ENDPOINTS.SEARCH_BY_USERNAME, {
      params: { username, page, limit }
    })
    return response.data
  } catch (error) {
    console.error('Error searching by username:', error)
    throw error
  }
}

export const getClientWalletStatement = async (params) => {
  try {
    const response = await api.post(API_ENDPOINTS.CLIENT_WALLET_STATEMENT, params)
    return response.data
  } catch (error) {
    console.error('Error fetching client wallet statement:', error)
    throw error
  }
}

export const declareResult = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.DECLARERESULT, data)
    return response.data
  } catch (error) {
    console.error('Error declaring result:', error)
    throw error
  }
}

export const voidBet = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID, data)
    return response.data
  } catch (error) {
    console.error('Error voiding bet:', error)
    throw error
  }
}

export const getOpenBetsForMarket = async (params) => {
  try {
    const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.OPEN_BETS, { params })
    return response.data
  } catch (error) {
    console.error('Error fetching open bets:', error)
    throw error
  }
}

export const voidSingleOpenBet = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_SINGLE_BET, data)
    return response.data
  } catch (error) {
    console.error('Error voiding single bet:', error)
    throw error
  }
}

export const getSettledMarkets = async (params) => {
  try {
    const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.SETTLED_MARKETS, { params })
    return response.data
  } catch (error) {
    console.error('Error fetching settled markets:', error)
    throw error
  }
}

export const getSettledBets = async (params) => {
  try {
    const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.SETTLED_BETS, { params })
    return response.data
  } catch (error) {
    console.error('Error fetching settled bets:', error)
    throw error
  }
}

export const voidMarketAfterSettlement = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_MARKET_AFTER_SETTLEMENT, data)
    return response.data
  } catch (error) {
    console.error('Error voiding market after settlement:', error)
    throw error
  }
}

export const voidBetAfterSettlement = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.INTERNALSETTLE.VOID_BET_AFTER_SETTLEMENT, data)
    return response.data
  } catch (error) {
    console.error('Error voiding bet after settlement:', error)
    throw error
  }
}

export default api

